import { useState, useMemo, useEffect } from "react";
import { Building2, Leaf, AlertCircle, AlertTriangle } from "lucide-react";
import { TabCompletionHeader } from "./common/TabCompletionHeader";
import { MetricCard } from "./common/MetricCard";
import { GeneralInfoSection } from "./general/GeneralInfoSection";
import { SustainabilitySection } from "./general/SustainabilitySection";
import { useWizard } from "@/contexts/WizardContext";
import { validateGeneralInfo, countIssues } from "./general/generalInfoValidation";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { WizardSectionNav } from "@/components/wizard/WizardSectionNav";
import { getSectionByCardKey } from "@shared/section-definitions";

export function GeneralTab() {
  const {
    generalOverallCompletion,
    generalInfoCardCompletion,
    sustainabilityCardCompletion,
    generalInfo,
    isLocked,
    currentSectionId,
    currentSection,
    goToSection,
  } = useWizard();
  const [expandedGeneralSection, setExpandedGeneralSection] = useState<string | null>(null);

  // Sync expandedGeneralSection with currentSection
  useEffect(() => {
    if (currentSection && currentSection.tab === "general") {
      setExpandedGeneralSection(currentSection.cardKey);
    }
  }, [currentSection]);

  const handleSectionExpand = (cardKey: string) => {
    setExpandedGeneralSection(cardKey);
    const section = getSectionByCardKey("general", cardKey);
    if (section) {
      goToSection(section.id);
    }
  };

  const handleSectionClose = () => {
    setExpandedGeneralSection(null);
  };

  const validationIssues = useMemo(() => validateGeneralInfo(generalInfo), [generalInfo]);
  const { errors, warnings } = useMemo(() => countIssues(validationIssues), [validationIssues]);

  const generalStatusLabel = isLocked ? "Section finalized" : "Section in progress";

  const sectionCompletions = {
    generalInfo: generalInfoCardCompletion,
    sustainability: sustainabilityCardCompletion,
  };

  return (
    <div className="flex gap-6">
      {/* Left Sidebar Navigation */}
      <div className="w-64 flex-shrink-0 hidden lg:block sticky top-[8.25rem] self-start h-[calc(100vh-16.5rem)]">
        <WizardSectionNav
          currentTab="general"
          currentSectionId={currentSectionId}
          onSectionClick={(section) => handleSectionExpand(section.cardKey)}
          sectionCompletions={sectionCompletions}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 space-y-4">
        <div>
          <TabCompletionHeader
            title="General Information"
            completion={generalOverallCompletion}
            statusLabel={generalStatusLabel}
          />
        </div>

        {/* Validation Issues Alert */}
        {validationIssues.length > 0 && (
          <Alert variant={errors > 0 ? "destructive" : "default"} className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <div className="font-semibold mb-2">
                {errors > 0 && (
                  <span className="text-red-600">
                    {errors} error{errors !== 1 ? "s" : ""} found
                  </span>
                )}
                {errors > 0 && warnings > 0 && <span className="mx-1">•</span>}
                {warnings > 0 && (
                  <span className="text-amber-600">
                    {warnings} warning{warnings !== 1 ? "s" : ""}
                  </span>
                )}
              </div>
              <ul className="list-disc list-inside space-y-1 text-sm">
                {validationIssues.slice(0, 5).map((issue, idx) => (
                  <li key={idx} className={issue.severity === "error" ? "text-red-700" : "text-amber-700"}>
                    <strong>{issue.field}:</strong> {issue.message}
                  </li>
                ))}
                {validationIssues.length > 5 && (
                  <li className="text-muted-foreground">
                    +{validationIssues.length - 5} more issues
                  </li>
                )}
              </ul>
              <p className="text-sm mt-2 text-muted-foreground">
                Go to &apos;General Information&apos; to fix these issues.
              </p>
            </AlertDescription>
          </Alert>
        )}

        {expandedGeneralSection === "generalInfo" && (
          <GeneralInfoSection onClose={handleSectionClose} />
        )}

        {expandedGeneralSection === "sustainability" && (
          <SustainabilitySection onClose={handleSectionClose} />
        )}

        {/* Collapsed Grid View */}
        {!expandedGeneralSection && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <MetricCard
              icon={Building2}
              title="General Information"
              description="Company profile, business activities, and key sustainability context"
              completion={generalInfoCardCompletion}
              onClick={() => handleSectionExpand("generalInfo")}
              badge={
                errors > 0 ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-full">
                    <AlertCircle className="h-3 w-3" />
                    {errors} error{errors !== 1 ? "s" : ""}
                  </span>
                ) : warnings > 0 ? (
                  <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-amber-700 bg-amber-100 rounded-full">
                    <AlertTriangle className="h-3 w-3" />
                    {warnings} warning{warnings !== 1 ? "s" : ""}
                  </span>
                ) : undefined
              }
            />
            <MetricCard
              icon={Leaf}
              title="Policies & targets"
              description="Policies, targets, and sustainability management practices"
              completion={sustainabilityCardCompletion}
              onClick={() => handleSectionExpand("sustainability")}
            />
          </div>
        )}
      </div>
    </div>
  );
}
